import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Phone, Mail, ChevronDown, Loader2 } from 'lucide-react';
import Button from '@/components/ui/Button';
import { COMPANY_INFO } from '@/lib/constants';
import { sendEstimationEmail } from '@/service/emailService';
import { toast } from 'sonner';

interface ContactFormProps {
  selectedPack?: string;
  selectedPrice?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ selectedPack, selectedPrice }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    pack: selectedPack || '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.pack) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsLoading(true);
    try {
      await sendEstimationEmail({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        budget: formData.pack,
        message: formData.company + ' - ' + formData.message,
        files: [],
      });
      
      toast.success('Votre message a été envoyé avec succès!');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        pack: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-20 lg:py-24 bg-white">
      <div className="container px-4 mx-auto">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Column - Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="text-left">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Parlons de votre <span className="text-orcsys-500">idée</span>
                </h2>
                <p className="text-lg text-slate-600">
                  Remplissez le formulaire et nous vous contacterons dans les plus brefs délais 
                  pour discuter de votre prototype.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-orcsys-50 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-orcsys-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Téléphone</p>
                    <a href="tel:+33123456789" className="text-lg font-semibold text-slate-900 hover:text-orcsys-500 transition-colors">
                      {COMPANY_INFO.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-orcsys-50 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-orcsys-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <a href="mailto:contact@orcsys.fr" className="text-lg font-semibold text-slate-900 hover:text-orcsys-500 transition-colors">
                      {COMPANY_INFO.email}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={isLoading}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-orcsys-500 focus:ring-2 focus:ring-orcsys-100 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isLoading}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-orcsys-500 focus:ring-2 focus:ring-orcsys-100 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={isLoading}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-orcsys-500 focus:ring-2 focus:ring-orcsys-100 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                      Entreprise
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      disabled={isLoading}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-orcsys-500 focus:ring-2 focus:ring-orcsys-100 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="pack" className="block text-sm font-medium text-slate-700 mb-2">
                    Formule sélectionnée *
                  </label>
                  <div className="relative">
                    <select
                      id="pack"
                      name="pack"
                      value={formData.pack}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-orcsys-500 focus:ring-2 focus:ring-orcsys-100 outline-none transition-all appearance-none bg-white pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Sélectionnez une formule</option>
                      <option value="Prototype Start">Prototype Start - 3 900 € HT</option>
                      <option value="Prototype Test">Prototype Test - 5 500 € HT</option>
                      <option value="Prototype Proof">Prototype Proof - 9 900 € HT</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    disabled={isLoading}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-orcsys-500 focus:ring-2 focus:ring-orcsys-100 outline-none transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Décrivez votre idée..."
                  ></textarea>
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full justify-center group"
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      Envoyer le message
                      <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;


